import { useCallback, useEffect, useRef, type ReactNode } from "react";
import { StatusIcon } from "./statusIcon.tsx";
import styles from "./modal.module.scss";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  wide?: boolean;
  closeOnBackdrop?: boolean;
  /**
   * Override the dialog's max-width beyond the built-in defaults (~560px / ~1100px `wide`).
   * Accepts any CSS width value (e.g. `"90vw"`, `1200`, `"calc(100vw - 2rem)"`).
   * Used when a form genuinely needs more horizontal room than `wide` provides
   * (RMA-class editors, multi-column dashboards). Prefer `wide` first.
   */
  maxWidth?: string | number;
}

export function Modal({ open, onClose, children, wide, maxWidth, closeOnBackdrop = true  }: ModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  /** Whether the current press started on the backdrop (the <dialog> itself). A drag that
   *  starts inside the content (text selection in an input) and is released over the
   *  backdrop fires `click` on the <dialog> — the common ancestor of the down/up targets —
   *  and must NOT count as a backdrop click. */
  const pressFromBackdropRef = useRef(false);

  useEffect(() => {
    const dlg = dialogRef.current;
    if (!dlg) return;
    if (open && !dlg.open) {
      dlg.showModal();
    } else if (!open && dlg.open) {
      dlg.close();
    }
  }, [open]);

  const handleCancel = useCallback(
    (e: React.SyntheticEvent) => {
      // Only treat cancel events originating on this dialog itself (the ESC
      // native-close) as a close request. A `cancel` event from a descendant
      // <input type="file"> bubbles here when the OS file picker is dismissed;
      // that must NOT close the surrounding Modal.
      if (e.target !== e.currentTarget) return;
      e.preventDefault();
      e.stopPropagation();
      onClose();
    },
    [onClose],
  );

  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLDialogElement>) => {
    pressFromBackdropRef.current = e.target === dialogRef.current;
  }, []);

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent<HTMLDialogElement>) => {
      const startedOnBackdrop = pressFromBackdropRef.current;
      pressFromBackdropRef.current = false;
      if (e.target === dialogRef.current && startedOnBackdrop) {
        e.stopPropagation();
        if (closeOnBackdrop) {
          onClose();
        }
      }
    },
    [closeOnBackdrop, onClose],
  );

  const dialogStyle = maxWidth != null
    ? { maxWidth: typeof maxWidth === "number" ? `${maxWidth}px` : maxWidth }
    : undefined;

  return (
    <dialog
      ref={dialogRef}
      className={`${styles.dialog}${wide ? ` ${styles.dialogWide}` : ""}`}
      style={dialogStyle}
      onCancel={handleCancel}
      onPointerDown={handlePointerDown}
      onClick={handleBackdropClick}
    >
      {children}
    </dialog>
  );
}

export function ModalHeader({ title, onClose, endAdornment }: { title: string; onClose?: () => void; endAdornment?: ReactNode }) {
  return (
    <div className={styles.header}>
      <h2 className={styles.title}>{title}</h2>
      <div className={styles.headerActions}>
        {endAdornment}
        {onClose && (
          <button className={styles.closeBtn} onClick={onClose} aria-label="Close" type="button">
            <StatusIcon kind="close" size={18} />
          </button>
        )}
      </div>
    </div>
  );
}

export function ModalBody({ children }: { children: ReactNode }) {
  return <div className={styles.body}>{children}</div>;
}

export function ModalFooter({ children }: { children: ReactNode }) {
  return <div className={styles.footer}>{children}</div>;
}
